import React from 'react';
import PropTypes from 'prop-types';
import { ProgressCircular } from '@americanexpress/dls-react';
import { IconCancelCircle, IconCheck } from '@americanexpress/dls-icons';
import { FormattedMessage } from 'react-intl';
import { formatBytes, formatFileName } from '../../utils/util';

// This component was created to reduce the maximum complexity requirement (12)
const PreviewOrDownloadButton = (formattedFileType) => {
  /* eslint-disable react/destructuring-assignment -- legacy pattern, refactor pending */
  // MT: Disable PDF preview, pending font fix for double-byte characters
  // ['PDF', 'GIF', 'BMP', 'JPG', 'JPEG', 'PNG'].includes(formattedFileType.toUpperCase())
  const isPreviewable = ['GIF', 'BMP', 'JPG', 'JPEG', 'PNG'].includes(
    formattedFileType.toUpperCase()
  );

  if (isPreviewable) {
    return <FormattedMessage id="preview" />;
  }

  return <FormattedMessage id="download" />;
  /* eslint-enable react/destructuring-assignment -- legacy pattern, refactor pending */
};

const DocList = ({
  acceptedDocs,
  handlePopupShow,
  docListView,
  filePreview,
  documentId,
  downloadProgress,
  suppressProofHeading = false,
}) => {
  let docMap = null;
  let allDocs = acceptedDocs;
  const uniqueId = new Date();
  const getDocSideLabel = (doc) => {
    const sideValue = (doc.side || doc.documentSide || doc.sideType || '')
      .toString()
      .toLowerCase();
    if (sideValue === 'front') {
      return 'Front';
    }
    if (sideValue === 'back') {
      return 'Back';
    }
    return '';
  };

  if (acceptedDocs.length > 0) {
    if (filePreview) {
      allDocs = acceptedDocs.map((doc) => {
        const dProg = downloadProgress.find((d) => d.fileId === doc.fileId);
        if (dProg) {
          return {
            ...doc,
            ...dProg,
          };
        }
        return {
          ...doc,
          ...{ downloadProg: null },
        };
      });
    }

    // eslint-disable-next-line complexity -- switch-heavy row rendering supports multiple legacy views
    docMap = allDocs.map((doc, idx) => {
      const match = /[^\\]*\.(\w+)$/.exec(doc.name);
      const formattedFileType = match ? match[1] : 'unknown';
      const currentView = docListView.toLowerCase();
      const sideLabel = getDocSideLabel(doc);

      switch (currentView) {
        case 'disabled':
          return null;
        case 'viewone':
          return (

            <div
              key={doc.fileId || doc.name}
              className="flex flex-align-center flex-align-self-stretch pad-1-b border-b pad-4-t dnuk-list-row"
            >
              <span
                className="flex flex-align-center flex-justify-center dls-white dls-bright-blue-bg dnuk-status-icon"
                aria-hidden="true"
              >
                <IconCheck size="2xs" color="white" title="uploaded" />
              </span>
              <div
                tabIndex="0"
                className="dls-grey-05 font-weight-normal dnuk-file-name"
              >
                <span data-testid={`${doc.name}ListId${idx}`} title={doc.name}>
                  {formatFileName(doc.name, 70)}
                </span>
              </div>
              <div className="flex flex-align-center dnuk-action-group">
                {doc.progress !== null ? (
                  <div
                    data-testid={`${doc.name}UploadProgress${idx}`}
                    className="dls-bright-blue"
                  >
                    {doc.progress === '100' || doc.progress === 0 ? (
                      <ProgressCircular key={0} />
                    ) : (
                      <ProgressCircular
                        key={1}
                        isDeterminate={true}
                        value={doc.progress}
                      />
                    )}
                  </div>
                ) : (
                  <div className="flex flex-align-center dnuk-action-group">
                    <span className="dls-grey-05 font-weight-bold dnuk-file-size">
                      {formatBytes(doc.size)}
                    </span>
                    {doc.downloadProg !== null
                      && doc.downloadProg < 100 ? null : (
                        <div className="flex flex-align-center dnuk-action-group">
                          <span className="dls-grey-02 font-weight-bold dnuk-separator">
                            |
                          </span>
                          <button
                            id={`deleteSection-${doc.fileId}`}
                            type="button"
                            tabIndex="0"
                            onClick={() => handlePopupShow(
                              idx,
                              doc.fileId,
                              doc.name,
                              true,
                              'delete'
                            )}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                handlePopupShow(
                                  idx,
                                  doc.fileId,
                                  doc.name,
                                  true,
                                  'delete'
                                );
                              }
                            }}
                            className="icon-hover dls-bright-blue text-align-center font-weight-normal dnuk-action-link"
                          >
                            <FormattedMessage id="deleteButton" />
                          </button>
                        </div>
                      )}
                    {filePreview && (
                      <div className="flex flex-align-center dnuk-action-group">
                        {doc.downloadProg !== null && doc.downloadProg < 100 ? (
                          <div>
                            <div
                              data-testid={`${doc.name}DownloadProgress${idx}`}
                              className="dls-bright-blue"
                            >
                              <ProgressCircular
                                isDeterminate={true}
                                value={doc.downloadProg}
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-align-center dnuk-action-group">
                            <span className="dls-grey-02 font-weight-bold dnuk-separator">
                              |
                            </span>
                            <button
                              id={`previewButton-${doc.fileId}`}
                              type="button"
                              tabIndex="0"
                              onClick={() => filePreview(
                                documentId,
                                doc.fileId,
                                doc.name,
                                doc.size,
                                uniqueId
                              )}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  filePreview(
                                    documentId,
                                    doc.fileId,
                                    doc.name,
                                    doc.size,
                                    uniqueId
                                  );
                                }
                              }}
                              className="icon-hover dls-bright-blue text-align-center font-weight-normal dnuk-action-link"
                            >
                              {PreviewOrDownloadButton(formattedFileType)}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        case 'viewproofkyc':
          return (

            <div
              key={doc.fileId || doc.name}
              className="flex flex-align-center flex-align-self-stretch border-b duak-proof-row"
            >
              {sideLabel ? (
                <div className="duak-state-id-side">{sideLabel}</div>
              ) : null}
              <div className="duak-proof-icon-container">
                <span
                  className="flex flex-align-center flex-justify-center dls-white dls-bright-blue-bg dnuk-status-icon"
                  aria-hidden="true"
                >
                  <IconCheck size="2xs" color="white" title="uploaded" />
                </span>
              </div>
              <div tabIndex="0" className="duak-proof-file-name">
                <span data-testid={`${doc.name}ListId${idx}`} title={doc.name}>
                  {formatFileName(doc.name, 70)}
                </span>
              </div>
              {doc.progress !== null ? (
                <div
                  data-testid={`${doc.name}UploadProgress${idx}`}
                  className="dls-bright-blue"
                >
                  {doc.progress === '100' || doc.progress === 0 ? (
                    <ProgressCircular key={0} />
                  ) : (
                    <ProgressCircular
                      key={1}
                      isDeterminate={true}
                      value={doc.progress}
                    />
                  )}
                </div>
              )
              // eslint-disable-next-line unicorn/no-nested-ternary -- preserves legacy view structure
                : sideLabel ? (
                  <>
                    <span className="dls-grey-05 font-weight-bold dnuk-file-size">
                      {formatBytes(doc.size)}
                    </span>
                    <span
                      aria-hidden="true"
                      className="border-dark-l duak-proof-action-separator"
                    />
                    <button
                      id={`deleteSection-${doc.fileId}`}
                      type="button"
                      tabIndex="0"
                      onClick={() => handlePopupShow(idx, doc.fileId, doc.name, true, 'delete')}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handlePopupShow(
                            idx,
                            doc.fileId,
                            doc.name,
                            true,
                            'delete'
                          );
                        }
                      }}
                      className="icon-hover duak-proof-action-button"
                    >
                      <FormattedMessage id="deleteButton" />
                    </button>
                    <span
                      aria-hidden="true"
                      className="border-dark-l duak-proof-action-separator"
                    />
                    <button
                      type="button"
                      className="icon-hover duak-proof-action-button"
                      onClick={() => filePreview(
                        documentId,
                        doc.fileId,
                        doc.name,
                        doc.size,
                        uniqueId
                      )}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          filePreview(
                            documentId,
                            doc.fileId,
                            doc.name,
                            doc.size,
                            uniqueId
                          );
                        }
                      }}
                    >
                      <FormattedMessage id="preview" />
                    </button>
                  </>
                ) : (
                  <div className="duak-proof-actions-row">
                    <span className="dls-grey-05 font-weight-bold dnuk-file-size">
                      {formatBytes(doc.size)}
                    </span>
                    <span
                      aria-hidden="true"
                      className="border-dark-l duak-proof-action-separator"
                    />
                    <button
                      id={`deleteSection-${doc.fileId}`}
                      type="button"
                      tabIndex="0"
                      onClick={() => handlePopupShow(idx, doc.fileId, doc.name, true, 'delete')}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handlePopupShow(
                            idx,
                            doc.fileId,
                            doc.name,
                            true,
                            'delete'
                          );
                        }
                      }}
                      className="icon-hover duak-proof-action-button"
                    >
                      <FormattedMessage id="deleteButton" />
                    </button>
                    <span
                      aria-hidden="true"
                      className="border-dark-l duak-proof-action-separator"
                    />
                    <button
                      type="button"
                      className="icon-hover duak-proof-action-button"
                      onClick={() => filePreview(
                        documentId,
                        doc.fileId,
                        doc.name,
                        doc.size,
                        uniqueId
                      )}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          filePreview(
                            documentId,
                            doc.fileId,
                            doc.name,
                            doc.size,
                            uniqueId
                          );
                        }
                      }}
                    >
                      <FormattedMessage id="preview" />
                    </button>
                  </div>
                )}
            </div>
          );
        default:
          return (

            <div
              key={doc.fileId || doc.name}
              className="flex flex-wrap flex-align-center pad-1-b"
            >
              {doc.progress !== null ? (
                <span
                  role="none"
                  className="dls-bright-blue progress-circle progress-indeterminate progress-sm margin-1-r"
                />
              ) : (
                <button
                  id="thirdButton"
                  tabIndex="0"
                  type="button"
                  onClick={() => handlePopupShow(idx, doc.fileId, doc.name, 'delete')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handlePopupShow(idx, doc.fileId, doc.name, 'delete');
                    }
                  }}
                  className="icon-hover margin-1-r"
                >
                  <IconCancelCircle
                    size="sm"
                    color="brand"
                    title="cancel circle icon"
                  />
                </button>
              )}
              {doc.name}
              <div tabIndex="0" className="margin-auto-l">
                {formatBytes(doc.size)}
              </div>
            </div>
          );
      }
    });
  }

  const shouldUseSideAwareProofRows = docListView.toLowerCase() === 'viewproofkyc'
    && allDocs.some((doc) => !!getDocSideLabel(doc));

  return (
    <div className="flex flex-align-self-stretch width-full">
      <div className="flex-column width-full">
        {(docListView.toLowerCase() === 'viewproofkyc'
          && acceptedDocs.length > 0
          && !shouldUseSideAwareProofRows
          && !suppressProofHeading) && (
          <div className="duak-proof-heading">Uploaded</div>
        )}
        {/* {docListView.toLowerCase() === 'viewone' && acceptedDocs.length > 0 && (
          <div className="font-weight-bold pad-1-b">
            Uploaded Files
          </div>
        )} */}
        {docMap}
      </div>
    </div>
  );
};
DocList.propTypes = {
  acceptedDocs: PropTypes.instanceOf(Array).isRequired,
  handlePopupShow: PropTypes.func.isRequired,
  docListView: PropTypes.string.isRequired,
  filePreview: PropTypes.func.isRequired,
  documentId: PropTypes.string.isRequired,
  downloadProgress: PropTypes.instanceOf(Array),
  // eslint-disable-next-line react/boolean-prop-naming -- prop name retained for backward compatibility
  suppressProofHeading: PropTypes.bool,
};
export default DocList;
